import React, { useState } from "react";
import { Oval } from "react-loader-spinner";

const ContactsPage = ({
  contactsList,
  isContactsLoading,
  categoryName,
  setCategoryName,
  manualContact,
  setManualContact,
  
  // Staging Props
  stagedContacts,
  handleStageManualContact,
  handleRemoveStagedContact,
  handleSaveStagedContacts,

  // Table Props
  handleDeleteRow,
  handleClearJson,
  handleUpdateCategory,
  handleUpdateContactJson,
  fileInputRef,
  handleImportFile,
  isUploading,

  // Campaign systems
  batchMessageData,
  setBatchMessageData,
  isSendingBatch,
  handleSendBatchByCategory
}) => {
  const [activeCategoryFilter, setActiveCategoryFilter] = useState("ALL");
  const [editingRowId, setEditingRowId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone_number: "" });

  const distinctCategories = ["ALL", ...new Set(contactsList.map(c => c.category).filter(Boolean))];

  // Professional SVG background for custom dropdowns
  const dropdownStyle = {
    appearance: "none",
    background: "#111827 url(\"data:image/svg+xml;utf8,<svg fill='%239ca3af' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>\") no-repeat right 12px center",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    color: "white",
    padding: "0.85rem 2.5rem 0.85rem 1rem",
    width: "100%",
    outline: "none",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "500"
  };

  const getParsedDescription = (descString) => {
    try {
      return typeof descString === "string" ? JSON.parse(descString) : descString;
    } catch (e) {
      return { name: "N/A", phone_number: "N/A" };
    }
  };

  const filteredContacts = contactsList.filter(item => {
    if (activeCategoryFilter === "ALL") return true;
    return item.category === activeCategoryFilter;
  });

  return (
    <div className="view-container">
      <div className="page-header" style={{ marginBottom: "2rem" }}>
        <h1>Workspace Contacts Directory</h1>
      </div>

      <div className="panel-grid">
        {/* =======================================
            STAGING AREA CARD (MANUAL + UPLOAD) 
        ======================================== */}
        <div className="card">
          <h2 className="card-title">Add & Stage Contacts</h2>
          
          <div style={{ marginBottom: "1.5rem" }}>
            <label className="form-label">Step 1: Define Target Category</label>
            <input 
              type="text" 
              className="form-input" 
              placeholder="e.g. VIP, Leads, Tech Team" 
              value={categoryName} 
              onChange={e => setCategoryName(e.target.value)} 
            />
          </div>

          <div style={{ borderBottom: "1px solid var(--border)", marginBottom: "1.5rem" }} />

          {/* Manual Entry Form */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label className="form-label">Step 2: Add Manually or Upload Bulk</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <input 
                type="text" 
                className="form-input" 
                placeholder="Full Name" 
                style={{ marginBottom: 0 }}
                value={manualContact.name} 
                onChange={e => setManualContact({...manualContact, name: e.target.value})} 
              />
              <input 
                type="tel" 
                className="form-input" 
                placeholder="2547..." 
                style={{ marginBottom: 0 }}
                value={manualContact.phone_number} 
                onChange={e => setManualContact({...manualContact, phone_number: e.target.value})} 
              />
            </div>
            
            <button type="button" className="btn-primary" style={{ background: "rgba(99, 102, 241, 0.15)", color: "var(--primary)" }} onClick={handleStageManualContact}>
              + Stage Manual Contact
            </button>
          </div>

          {/* Excel File Uploader */}
          <input type="file" accept=".xlsx, .xls, .csv" style={{ display: "none" }} ref={fileInputRef} onChange={handleImportFile} />
          
          <div 
            style={{ border: '2px dashed var(--border)', padding: '1rem', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', background: 'rgba(0,0,0,0.1)', marginBottom: "0.75rem" }} 
            onClick={() => fileInputRef.current.click()}
          >
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {isUploading ? "Processing Spreadsheet..." : "Click to Upload Excel/CSV Manifest"}
            </span>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: "1.5rem" }}>
            <a href="/contacts.xlsx" download="contacts_template.xlsx" style={{ color: 'var(--primary)', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 500 }}>
              ↓ Download Spreadsheet Template
            </a>
          </div>

          <div style={{ borderBottom: "1px solid var(--border)", marginBottom: "1.5rem" }} />

          {/* Staged Preview List */}
          <label className="form-label">Step 3: Review Staged Contacts</label>
          <div style={{ background: "#0b0f1a", border: "1px solid var(--border)", borderRadius: "8px", maxHeight: "180px", overflowY: "auto", marginBottom: "1rem" }}>
            {stagedContacts.length === 0 ? (
              <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                No contacts staged yet.
              </div>
            ) : (
              stagedContacts.map((contact, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ color: "white", fontWeight: 500, marginRight: '10px' }}>{contact.name}</span>
                    <span style={{ color: "var(--text-muted)", fontFamily: "monospace" }}>{contact.phone_number}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: "rgba(16, 185, 129, 0.1)", color: "var(--success)", padding: "2px 6px", borderRadius: "4px", fontSize: "0.7rem" }}>
                      {contact.category}
                    </span>
                    <button type="button" onClick={() => handleRemoveStagedContact(index)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontWeight: 'bold' }}>
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <button 
            type="button" 
            className="btn-primary" 
            disabled={stagedContacts.length === 0 || isContactsLoading}
            onClick={handleSaveStagedContacts}
          >
            {isContactsLoading ? <Oval height={18} width={18} color="#fff" /> : `Save Contacts Now (${stagedContacts.length})`}
          </button>
        </div>


        {/* =======================================
            BATCH TARGETED CAMPAIGNING SYSTEM 
        ======================================== */}
        <div className="card">
          <h2 className="card-title">Targeted Batch Dispatches</h2>
          
          <label className="form-label">Select Target Group Category</label>
          <select 
            style={{ ...dropdownStyle, marginBottom: "1rem" }}
            onChange={(e) => setActiveCategoryFilter(e.target.value)}
            value={activeCategoryFilter}
          >
            {distinctCategories.map((cat, i) => (
              <option key={i} value={cat}>{cat === "ALL" ? "All Contacts Group" : cat}</option>
            ))}
          </select>

          {/* Live numbers preview indicator & list */}
          <div style={{ background: "rgba(99, 102, 241, 0.1)", border: "1px solid rgba(99, 102, 241, 0.2)", borderRadius: "8px", padding: "1rem", marginBottom: "0.75rem" }}>
            <span style={{ fontSize: "0.85rem", color: "var(--primary)", fontWeight: "bold" }}>
              Target Batch Size: {filteredContacts.length} numbers parsed
            </span>
          </div>

          {/* SCROLLABLE CATEGORY PREVIEW LIST */}
          <div style={{ maxHeight: "160px", overflowY: "auto", background: "#0b0f1a", border: "1px solid var(--border)", padding: "10px", borderRadius: "8px", marginBottom: "1.5rem" }}>
            {filteredContacts.length === 0 ? (
               <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem", padding: "1rem 0" }}>Empty Category</div>
            ) : (
              filteredContacts.map((c, i) => {
                const inner = getParsedDescription(c.description);
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--border)", padding: "6px 0", fontSize: "0.8rem" }}>
                    <span style={{ color: "var(--text-main)" }}>{inner.name || <em style={{color: 'var(--text-muted)'}}>No Name</em>}</span>
                    <span style={{ fontFamily: "monospace", color: "var(--text-muted)" }}>{inner.phone_number || "N/A"}</span>
                  </div>
                );
              })
            )}
          </div>

          <label className="form-label">Broadcast Content</label>
          <textarea 
            className="form-textarea" 
            placeholder="Type your batch SMS message here..." 
            rows="5"
            value={batchMessageData.message}
            onChange={e => setBatchMessageData({...batchMessageData, message: e.target.value})}
          />
          <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '-8px', marginBottom: '1rem' }}>{batchMessageData.message.length} characters</div>

          <label className="form-label">Category</label>
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="radio" name="batchSendType" value="now" checked={batchMessageData.sendType === "now"} onChange={e => setBatchMessageData({ ...batchMessageData, sendType: e.target.value })} /> send now
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="radio" name="batchSendType" value="scheduled" checked={batchMessageData.sendType === "scheduled"} onChange={e => setBatchMessageData({ ...batchMessageData, sendType: e.target.value })} /> schedule for later
            </label>
          </div>

          {batchMessageData.sendType === "scheduled" && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Timestamp</label>
              <input type="datetime-local" className="form-input" value={batchMessageData.scheduledTime} onChange={e => setBatchMessageData({ ...batchMessageData, scheduledTime: e.target.value })} />
            </div>
          )}

          <button 
            type="button" 
            className="btn-primary" 
            disabled={isSendingBatch || filteredContacts.length === 0}
            onClick={() => handleSendBatchByCategory(activeCategoryFilter)}
          >
            {isSendingBatch ? <Oval height={18} width={18} color="#fff" /> : `Dispatch Batch Message`}
          </button>
        </div>
      </div>


      {/* =======================================
            MATRIX DIRECTORY LIST VIEW
      ======================================== */}
      <div className="card" style={{ marginTop: "2.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <h2 className="card-title" style={{ margin: 0 }}>Registered Directories</h2>
          
          <div style={{ width: "250px" }}>
            <select 
              style={{ ...dropdownStyle, padding: "0.6rem 2.5rem 0.6rem 1rem", fontSize: "0.85rem" }}
              onChange={(e) => setActiveCategoryFilter(e.target.value)}
              value={activeCategoryFilter}
            >
              {distinctCategories.map((cat, idx) => (
                <option key={idx} value={cat}>{cat === "ALL" ? "View All Categories" : cat}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ width: "100%", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)", color: "var(--text-muted)" }}>
                <th style={{ padding: "1rem" }}>Row ID</th>
                <th style={{ padding: "1rem" }}>Category</th>
                <th style={{ padding: "1rem" }}>Contact ID (Inner JSON)</th>
                <th style={{ padding: "1rem" }}>Parsed Name</th>
                <th style={{ padding: "1rem" }}>Phone Number</th>
                <th style={{ padding: "1rem", textAlign: "right" }}>Actions Matrix</th>
              </tr>
            </thead>
            <tbody>
              {isContactsLoading && contactsList.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "3rem" }}>
                    <div style={{ display: "flex", justifyContent: "center" }}><Oval height={30} width={30} color="var(--primary)" /></div>
                  </td>
                </tr>
              ) : filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                    No contacts matched the filter selections.
                  </td>
                </tr>
              ) : (
                filteredContacts.map((contact) => {
                  const innerJson = getParsedDescription(contact.description);
                  const isEditing = editingRowId === contact.id;

                  return (
                    <tr key={contact.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "1rem", fontFamily: "monospace" }}>{contact.id}</td>
                      <td style={{ padding: "1rem" }}>
                        <span style={{ background: "rgba(255,255,255,0.05)", padding: "4px 8px", borderRadius: "4px", border: "1px solid var(--border)" }}>
                          {contact.category || "General"}
                        </span>
                      </td>
                      <td style={{ padding: "1rem", fontFamily: "monospace", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                        {innerJson.id || "N/A"}
                      </td>
                      <td style={{ padding: "1rem" }}>
                        {isEditing ? (
                          <input 
                            type="text" 
                            className="form-input" 
                            style={{ marginBottom: 0, padding: "4px 8px" }} 
                            value={editForm.name} 
                            onChange={e => setEditForm({...editForm, name: e.target.value})} 
                          />
                        ) : (
                          innerJson.name || <em style={{ color: "var(--text-muted)" }}>Empty</em>
                        )}
                      </td>
                      <td style={{ padding: "1rem", fontFamily: "monospace" }}>
                        {isEditing ? (
                          <input 
                            type="text" 
                            className="form-input" 
                            style={{ marginBottom: 0, padding: "4px 8px" }} 
                            value={editForm.phone_number} 
                            onChange={e => setEditForm({...editForm, phone_number: e.target.value})} 
                          />
                        ) : (
                          innerJson.phone_number || <em style={{ color: "var(--text-muted)" }}>Empty</em>
                        )}
                      </td>
                      <td style={{ padding: "1rem", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                          {isEditing ? (
                            <>
                              <button type="button" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#10b981", border: "1px solid rgba(16, 185, 129, 0.2)", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.75rem", fontWeight: "bold" }} onClick={() => { handleUpdateContactJson(contact.id, editForm); setEditingRowId(null); }}>
                                Save JSON
                              </button>
                              <button type="button" style={{ background: "rgba(99, 102, 241, 0.1)", color: "var(--primary)", border: "1px solid rgba(99, 102, 241, 0.2)", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.75rem", fontWeight: "bold" }} onClick={() => { handleUpdateCategory(contact.id); setEditingRowId(null); }}>
                                Save Group
                              </button>
                              <button type="button" style={{ background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border)", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.75rem" }} onClick={() => setEditingRowId(null)}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" style={{ background: "rgba(56, 189, 248, 0.1)", color: "#38bdf8", border: "1px solid rgba(56, 189, 248, 0.2)", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.75rem", fontWeight: "bold" }} onClick={() => { setEditingRowId(contact.id); setEditForm({ name: innerJson.name || "", phone_number: innerJson.phone_number || "" }); }}>
                                Edit
                              </button>
                              <button type="button" style={{ background: "rgba(245, 158, 11, 0.1)", color: "var(--warning)", border: "1px solid rgba(245, 158, 11, 0.2)", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.75rem", fontWeight: "bold" }} onClick={() => handleClearJson(contact.id)}>
                                Clear JSON
                              </button>
                              <button type="button" style={{ background: "rgba(239, 68, 68, 0.1)", color: "var(--danger)", border: "1px solid rgba(239, 68, 68, 0.2)", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.75rem", fontWeight: "bold" }} onClick={() => handleDeleteRow(contact.id)}>
                                Delete Row
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContactsPage;